import React, {FC, useEffect, useMemo, useState} from 'react'
import {CustomModal} from '_metronic/partials/modals/CustomModal'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import Input from 'app/components/Input'
import Select from '../Select'
import {MenuListProps, OptionProps, SingleValue, SingleValueProps} from 'react-select'
import {toAbsoluteUrl} from '_metronic/helpers'
import {useAuth} from 'app/modules/auth'
import {deleteObject, getDownloadURL, ref, uploadBytes} from 'firebase/storage'
import db, {storage} from 'services/firebase'
import Dropzone from 'react-dropzone'
import {FilterOptionOption} from 'react-select/dist/declarations/src/filters'
import {addDoc, collection, doc, serverTimestamp, setDoc, updateDoc} from '@firebase/firestore'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'
import {SubprocessorDataType} from '../Subprocessors'
import MultiSelect from '../MultiSelect'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  isEditMode: boolean
  subprocessor?: SubprocessorDataType
  availableSubprocessors: {id: string; title: string; category: string; logoURL: string}[]
}

type CustomOptionType = {
  id?: string
  title: string
  logoURL?: string
}

const CustomOptionComponent = (props: OptionProps<CustomOptionType>) => {
  const {data, innerRef, innerProps} = props

  return (
    <div
      ref={innerRef}
      className='d-flex align-items-center cursor-pointer'
      style={{
        gap: '8px',
      }}
      {...innerProps}
    >
      <img
        alt={`${data.title}-logo`}
        src={toAbsoluteUrl(data.logoURL as string)}
        className=''
        style={{height: '16px', width: '16px'}}
      />
      <span className='text-dark fs-6'>{data.title}</span>
    </div>
  )
}
const menuListComponent = (props: MenuListProps<CustomOptionType>) => {
  const {children} = props

  const standardChildren: React.ReactNode[] = []
  let customChild: React.ReactNode | null = null

  React.Children.forEach(children, (child: any) => {
    if (child?.props?.data?.title.toLowerCase() === 'add manually') {
      customChild = child
    } else {
      standardChildren.push(child)
    }
  })

  return (
    <div
      style={{
        padding: '16px',
      }}
    >
      <div
        style={{
          maxHeight: '150px',
          overflowY: 'auto',
          display: 'flex',
          gap: '12px',
          flexDirection: 'column',
        }}
      >
        {standardChildren}
      </div>

      {customChild &&
        (standardChildren.length > 0 ? (
          <div
            style={{
              marginTop: '12px',
              borderTop: '1px solid #E5E7EB',
              paddingTop: '12px',
            }}
          >
            {customChild}
          </div>
        ) : (
          <div>{customChild}</div>
        ))}
    </div>
  )
}

const SingleValueComponent = (props: SingleValueProps<CustomOptionType>) => {
  const {data, innerProps} = props
  return (
    <div
      className='d-flex align-items-center '
      style={{gap: '8px', position: 'absolute', left: '13px'}}
      {...innerProps}
    >
      <img
        alt={`${data.title}-logo`}
        src={toAbsoluteUrl(data.logoURL as string)}
        className=''
        style={{height: '16px', width: '16px'}}
      />
      <span className='fw-medium fs-6' style={{color: '#4b5675'}}>
        {data.title}
      </span>
    </div>
  )
}

const filterOption = (option: FilterOptionOption<CustomOptionType>, inputValue: string) => {
  return option.data.title.toLowerCase().includes(inputValue.toLowerCase())
}

const SUPPORTED_IMAGE_FORMATS = ['image/jpg', 'image/jpeg', 'image/png']

const initialValues = {
  id: '',
  title: '',
  logoName: '',
  logoURL: '',
  category: '',
  hostingRegions: [],
}

const validationSchema = Yup.object({
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Name is required'),
  logoURL: Yup.mixed<File | string>()
    .required('Logo is required')
    .test('fileFormat', 'Unsupported format for logo', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && SUPPORTED_IMAGE_FORMATS.includes(value.type)
      }
      return false
    })
    .test('fileSize', 'Logo size is too large', (value) => {
      if (typeof value === 'string') return true

      if (value instanceof File) {
        return value && value.size <= 5 * 1024 * 1024
      }
      return false
    }),
  hostingRegions: Yup.array().min(1, 'Please select at least one hosting region'),
})

const AddEditSubprocessorModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  isEditMode,
  subprocessor,
  availableSubprocessors,
}) => {
  const {currentUser} = useAuth()

  const subprocessors: Array<CustomOptionType> = availableSubprocessors?.map((curr) => ({
    id: curr.id,
    title: curr.title,
    logoURL: curr.logoURL,
  }))

  const [loading, setLoading] = useState(false)

  const [isCustomSubprocessorOptionSelected, setIsCustomSubprocessorOptionSelected] =
    useState(false)

  const [uploadedFile, setUploadedFile] = useState<File>()

  const [selectedOption, setSelectedOption] = useState<SingleValue<CustomOptionType>>()

  const [selectError, setSelectError] = useState('')
  const [categorySelectError, setCategorySelectError] = useState('')
  const [hostingRegionError, setHostingRegionError] = useState('')

  const [isDropzoneShownForEditMode, setIsDropzoneShownForEditMode] = useState(false)

  const [countriesList, setAllCountriesList] = useState<Array<string>>([])

  const subprocessorsCategoryList = useMemo(() => {
    return Array.from(new Set(availableSubprocessors.map((subprocessor) => subprocessor.category)))
      .map((category) => ({label: category, value: category}))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [availableSubprocessors])

  const getAllCountriesName = async () => {
    try {
      const response = await fetch('https://restcountries.com/v3.1/all?fields=name,idd')
      if (response.ok) {
        const data = await response.json()
        const countryNames = data.map((country) => country.name.common).sort()
        setAllCountriesList(countryNames)
      }
    } catch (err) {
      console.error('Error while fetching countries list for subprocessor hosting regions')
    }
  }

  useEffect(() => {
    getAllCountriesName()
  }, [])

  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth() + 1
  const day = currentDate.getDate()
  const todaysDate = month + '/' + day + '/' + year

  const formik = useFormik<SubprocessorDataType>({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const subprocessorsRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'subprocessors',
          'all_subprocessors'
        )

        if (!isEditMode) {
          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.logoURL instanceof File) {
            fileName = values.logoURL.name
            fileId = `${Date.now()}`
            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/subprocessors/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.logoURL)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          const newDocRef = await addDoc(subprocessorsRef, {
            title: values.title,
            category: values.category,
            hostingRegions: values.hostingRegions,
            logoName: downloadURL.length > 0 ? fileName : values.logoName,
            logoURL: downloadURL.length > 0 ? downloadURL : values.logoURL,
            logoId: downloadURL.length > 0 ? fileId : '',
            isCustom: downloadURL.length > 0 ? true : false,
          })

          await updateDoc(newDocRef, {
            id: newDocRef.id,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${values.title} subprocessor for trust center has been added`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Subprocessor added',
          })

          showSuccessToast({message: 'New subprocessor has been added successfully'})
        } else {
          const docRef = doc(subprocessorsRef, values.id)
          let isOldFileDeleted = false

          if (subprocessor?.isCustom && subprocessor?.logoURL !== values.logoURL) {
            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/subprocessors/${subprocessor?.logoId}-${subprocessor?.logoName}`
            )

            await deleteObject(fileRef)

            isOldFileDeleted = true
          }

          let downloadURL = ''
          let fileName = ''
          let fileId = ''

          if (values.logoURL instanceof File) {
            fileName = values.logoURL.name
            fileId = `${Date.now()}`

            const fileRef = ref(
              storage,
              `tenants/${currentUser?.tenantId}/trust_center/subprocessors/${fileId}-${fileName}`
            )
            const uploadResult = await uploadBytes(fileRef, values.logoURL!)
            downloadURL = await getDownloadURL(uploadResult.ref)
          }

          await setDoc(docRef, {
            id: docRef.id,
            title: values.title,
            category: values.category,
            hostingRegions: values.hostingRegions,
            logoName: downloadURL.length > 0 ? fileName : isOldFileDeleted ? '' : values.logoName,
            logoURL: downloadURL.length > 0 ? downloadURL : values.logoURL,
            logoId: downloadURL.length > 0 ? fileId : isOldFileDeleted ? '' : values.logoId,
            isCustom: downloadURL.length > 0 ? true : isOldFileDeleted ? false : values?.isCustom,
          })

          const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
          await addDoc(activitiesRef, {
            message: `${subprocessor?.title} subprocessor for trust center has been updated`,
            timestamp: serverTimestamp(),
            tenantId: currentUser?.tenantId,
            uid: currentUser?.uid,
            username: currentUser?.userName,
            date: todaysDate,
            email: currentUser?.email,
            target: 'Trust Center',
            action: 'Subprocessor updated',
          })

          showSuccessToast({message: 'Subprocessor has been updated successfully'})
        }
      } catch (error) {
        console.log('Error while updating subprocessor', error)
      } finally {
        setLoading(false)
        hideModal()
      }
    },
  })

  useEffect(() => {
    if (subprocessor?.title) {
      formik.setValues({
        id: subprocessor!.id,
        title: subprocessor!.title,
        category: subprocessor!.category,
        hostingRegions: subprocessor!.hostingRegions,
        logoName: subprocessor.logoName,
        logoURL: subprocessor!.logoURL as string,
        logoId: subprocessor!.logoId,
        isCustom: subprocessor.isCustom ?? false,
      })
    }

    setSelectedOption(() => {
      return isEditMode
        ? subprocessor?.isCustom ?? false
          ? {
              title: 'Add Manually',
              logoURL: '/media/icon/cube.svg',
            }
          : {
              id: subprocessor?.id,
              title: subprocessor!.title,
              logoURL: subprocessor!.logoURL as string,
            }
        : null
    })

    if (subprocessor?.isCustom) {
      setIsCustomSubprocessorOptionSelected(true)
    }
  }, [subprocessor])

  const okBtnClickHandler = () => {
    if (!selectedOption) {
      setSelectError('Subprocessor is required')
    }

    if (!formik.values.category) {
      setCategorySelectError('Category is required')
    }

    if (formik.values.hostingRegions.length === 0) {
      setHostingRegionError('Please select at least one hosting region')
      // return
    }

    formik.submitForm()
  }

  const handleSubprocessorChange = (option: SingleValue<CustomOptionType>) => {
    setSelectedOption(option)
    setSelectError('')
    if (option?.title.toLowerCase() === 'add manually') {
      setIsCustomSubprocessorOptionSelected(true)
      formik.setFieldValue('title', '')
      formik.setFieldValue('logoURL', null)
      formik.setFieldValue('category', '')
    } else {
      setIsCustomSubprocessorOptionSelected(false)
      formik.setFieldValue('title', option?.title)
      formik.setFieldValue('logoURL', option?.logoURL)
      formik.setFieldValue(
        'category',
        availableSubprocessors.filter((curr) => curr.id === option?.id)[0].category
      )
    }
  }

  const handleRemoveFile = () => {
    setUploadedFile(undefined)
    formik.setFieldValue('logoURL', null)
    setIsDropzoneShownForEditMode(true)
  }

  const handleUpload = (acceptedFile: File[]) => {
    setUploadedFile(acceptedFile[0])
    formik.setFieldTouched('logoURL', true)
    formik.setFieldValue('logoURL', acceptedFile[0])
  }

  const toggleHostingRegions = (title: string) => {
    const currentSelectedHostingRegions = formik.values.hostingRegions
    const isFound = currentSelectedHostingRegions.some((name) => name === title)

    if (isFound) {
      formik.setFieldValue('hostingRegions', [
        ...formik.values.hostingRegions.filter((name) => name !== title),
      ])

      if (currentSelectedHostingRegions.length === 1) {
        setHostingRegionError('Please select at least one hosting region')
      }
    } else {
      formik.setFieldValue('hostingRegions', [...formik.values.hostingRegions, title])
      setHostingRegionError('')
    }
  }

  const removeHostingRegion = (title: string) => {
    formik.setFieldValue('hostingRegions', [
      ...formik.values.hostingRegions.filter((name) => name !== title),
    ])

    if (formik.values.hostingRegions.length === 1) {
      setHostingRegionError('Please select at least one hosting region')
    }
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={`${isEditMode ? 'Edit Subprocessor' : 'Add Subprocessor'}`}
      okBtnText={`${isEditMode ? 'Save' : 'Add Subprocessor'}`}
      okBtnClickHandler={okBtnClickHandler}
      isCloseIconShown={true}
      isLoading={loading}
      isLoadingBtnText={`${isEditMode ? 'Saving...' : 'Adding Subprocessor...'}`}
    >
      <div
        className='d-flex flex-column align-items-center justify-content-center'
        style={{gap: '20px'}}
      >
        <Select<CustomOptionType>
          label='Select Subprocessor'
          options={[
            ...subprocessors,
            {
              title: 'Add Manually',
              logoURL: '/media/icon/cube.svg',
            },
          ]}
          placeholder='Select Subprocessor'
          optionComponent={CustomOptionComponent}
          singleValueComponent={SingleValueComponent}
          menuListComponent={menuListComponent}
          filterOption={filterOption}
          onChange={(value) => handleSubprocessorChange(value as SingleValue<CustomOptionType>)}
          value={selectedOption}
          isDisabled={isEditMode}
          isClearable={false}
          error={selectError}
        />

        {isCustomSubprocessorOptionSelected && (
          <>
            <Input
              type='text'
              label='Subprocessor Name'
              placeholder='Subprocessor Name'
              error={formik.touched.title && formik.errors.title ? formik.errors.title : ''}
              {...formik.getFieldProps('title')}
            />

            <Select
              label='Subprocessor Category'
              placeholder='Select Subprocessor Category'
              options={subprocessorsCategoryList}
              onChange={(item) => {
                if (item) {
                  const selectedOption = item as SingleValue<{label: string; value: string}>
                  formik.setFieldValue('category', selectedOption?.value)
                  setCategorySelectError('')
                }
              }}
              value={
                formik.values.category
                  ? {label: formik.values.category, value: formik.values.category}
                  : null
              }
              error={categorySelectError}
              isClearable={false}
            />

            <div className='w-100 d-flex flex-column' style={{gap: '8px'}}>
              <label className='fw-medium text-dark fs-6'>Logo</label>

              {(isEditMode && subprocessor?.isCustom && !isDropzoneShownForEditMode) ||
              uploadedFile ? (
                <div
                  className=''
                  style={{border: '1px solid #E5E7EB', borderRadius: '8px', padding: '16px'}}
                >
                  <div className='d-flex align-items-center' style={{gap: '16px'}}>
                    <img
                      src={
                        uploadedFile
                          ? URL.createObjectURL(uploadedFile as File)
                          : toAbsoluteUrl(subprocessor?.logoURL as string)
                      }
                      alt='subprocessor-image'
                      className='object-fit-cover'
                      style={{borderRadius: '12px', width: '48px', height: '48px'}}
                    />

                    <span className='flex-fill text-dark fs-6 fw-medium'>
                      {uploadedFile?.name ?? subprocessor?.logoName}
                    </span>

                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={handleRemoveFile}
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                </div>
              ) : (
                <Dropzone
                  onDrop={handleUpload}
                  minSize={1024}
                  accept={{
                    'image/png': ['.png'],
                    'image/jpeg': ['.jpg', '.jpeg'],
                  }}
                  maxSize={5 * 1024 * 1024}
                >
                  {({getRootProps, getInputProps}) => (
                    <div
                      {...getRootProps({
                        className: 'dropzone',
                        style: {
                          backgroundColor: 'white',
                          border: '2px dashed #E5E7EB',
                          borderRadius: '8px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '24px 32px',
                          textAlign: 'center',
                          cursor: 'pointer',
                        },
                      })}
                    >
                      <input {...getInputProps()} />

                      <div className='d-flex flex-column align-items-center' style={{gap: '16px'}}>
                        <div className='d-flex flex-column align-items-center' style={{gap: '7px'}}>
                          <div
                            className='d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: '#F3F4F6',
                              borderRadius: '12px',
                              height: '48px',
                              width: '48px',
                            }}
                          >
                            <img
                              alt='image-placeholder-icon'
                              src={toAbsoluteUrl('/media/icon/image-placeholder.svg')}
                              className=''
                            />
                          </div>

                          <span className='text-dark fw-medium fs-6'>Drag and drop file here</span>

                          <span className='text-secondary-gray' style={{fontSize: '13px'}}>
                            48x48 px recommended. Supported files: jpg, jpeg, png
                          </span>
                        </div>

                        <div>
                          <Button>
                            <img
                              alt='upload-icon'
                              src={toAbsoluteUrl('/media/icon/upload.svg')}
                              className=''
                              style={{height: '16px', width: '16px'}}
                            />
                            <span className='text-dark fs-6 fw-medium'>Upload File</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </Dropzone>
              )}

              {formik.touched.logoURL && formik.errors.logoURL && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.logoURL}</span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        <MultiSelect
          label='Hosting Regions'
          placeholder='Select Hosting Regions'
          options={countriesList.map((country) => ({id: country, title: country}))}
          selectedList={formik.values.hostingRegions}
          toggleList={toggleHostingRegions}
          removeItem={removeHostingRegion}
          error={hostingRegionError}
        />
      </div>
    </CustomModal>
  )
}

export default AddEditSubprocessorModal
